/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    Trophy,
    Timer,
    Sparkles,
    RefreshCw,
    CheckCircle,
    XCircle,
    ChevronRight,
    HelpCircle,
    Star,
    Shield,
    ArrowRight,
    Award,
    Share2,
    Copy,
    Check,
    Search,
    User
} from 'lucide-react';
import { Avatar } from './Avatar';
import { db, OperationType, handleFirestoreError } from '../firebase';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';

import { QuizQuestion, getDailyQuestions, ALL_QUESTIONS } from './fallbackQuestions';
import { QUIZ_TRANSLATIONS } from './quizTranslations';

interface FootballQuizProps {
    user: any;
    packs: number;
    setPacks: (v: number) => void;
    lang: string;
    onGrantSparks?: () => void;
    userQuizHistory?: Record<string, { score: number; rewarded: boolean; correctCount?: number; shareRewarded?: boolean }>;
    onSaveQuizResult?: (date: string, score: number, rewarded: boolean, correctCount: number) => Promise<void>;
    onClaimShareReward?: (date: string) => Promise<void> | void;
    onResetQuiz?: (date: string) => Promise<void>;
}

export const FootballQuiz = ({
                                 user,
                                 packs,
                                 setPacks,
                                 lang,
                                 userQuizHistory = {},
                                 onSaveQuizResult,
                                 onClaimShareReward,
                                 onResetQuiz
                             }: FootballQuizProps) => {
    const dateString = new Date().toISOString().split('T')[0];

    const t_quiz = (key: string, replacements?: Record<string, string | number>) => {
        const currentLang = QUIZ_TRANSLATIONS[lang] ? lang : 'en';
        let text = QUIZ_TRANSLATIONS[currentLang]?.[key] || QUIZ_TRANSLATIONS['en']?.[key] || key;
        if (replacements) {
            Object.entries(replacements).forEach(([k, val]) => {
                text = text.replace(`{${k}}`, String(val));
            });
        }
        return text;
    };

    const [dailyQuestions, setDailyQuestions] = useState<QuizQuestion[]>([]);
    const [loadingQuestions, setLoadingQuestions] = useState<boolean>(true);
    const [currentIdx, setCurrentIdx] = useState<number>(0);
    const [timer, setTimer] = useState<number>(10);
    const [score, setScore] = useState<number>(0);
    const [correctCount, setCorrectCount] = useState<number>(0);
    const [userAnswers, setUserAnswers] = useState<(string | null)[]>(new Array(10).fill(null));
    const [copied, setCopied] = useState<boolean>(false);
    const [shared, setShared] = useState<boolean>(false);

    // Scoreboard / Leaderboard Integration
    interface ScoreboardEntry {
        userId: string;
        displayName: string;
        photoURL: string;
        email: string;
        todayScore: number | null;
        totalScore: number;
        quizzesPlayed: number;
    }

    const [scoreboard, setScoreboard] = useState<ScoreboardEntry[]>([]);
    const [loadingScoreboard, setLoadingScoreboard] = useState<boolean>(true);
    const [scoreboardTab, setScoreboardTab] = useState<'today' | 'alltime'>('today');
    const [searchTerm, setSearchTerm] = useState<string>('');

    const fetchScoreboard = async () => {
        setLoadingScoreboard(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'quizleaderboard'));
            const entriesMap: Record<string, ScoreboardEntry> = {};

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const docUserId = data.userId;
                const docDate = data.date;
                const docScore = typeof data.score === 'number' ? data.score : 0;

                if (!docUserId) return;

                const isToday = docDate === dateString;

                if (!entriesMap[docUserId]) {
                    entriesMap[docUserId] = {
                        userId: docUserId,
                        displayName: data.displayName || 'Anonymous',
                        photoURL: data.photoURL || '',
                        email: data.email || '',
                        todayScore: null,
                        totalScore: 0,
                        quizzesPlayed: 0
                    };
                }

                if (isToday) {
                    entriesMap[docUserId].todayScore = docScore;
                }

                entriesMap[docUserId].totalScore += docScore;
                entriesMap[docUserId].quizzesPlayed += 1;
            });

            setScoreboard(Object.values(entriesMap));
        } catch (error) {
            console.error("Failed to load quiz scoreboard from quizleaderboard:", error);
        } finally {
            setLoadingScoreboard(false);
        }
    };

    const filteredScoreboard = useMemo(() => {
        return scoreboard
            .filter(entry => {
                if (searchTerm.trim() !== '') {
                    const search = searchTerm.toLowerCase();
                    const nameMatch = entry.displayName.toLowerCase().includes(search);
                    const emailMatch = entry.email.toLowerCase().includes(search);
                    return nameMatch || emailMatch;
                }
                return true;
            })
            .filter(entry => {
                if (scoreboardTab === 'today') {
                    return entry.todayScore !== null;
                }
                return true;
            })
            .sort((a, b) => {
                if (scoreboardTab === 'today') {
                    const scoreA = a.todayScore ?? -1;
                    const scoreB = b.todayScore ?? -1;
                    if (scoreB !== scoreA) {
                        return scoreB - scoreA;
                    }
                    return b.totalScore - a.totalScore;
                } else {
                    if (b.totalScore !== a.totalScore) {
                        return b.totalScore - a.totalScore;
                    }
                    return b.quizzesPlayed - a.quizzesPlayed;
                }
            });
    }, [scoreboard, scoreboardTab, searchTerm]);

    const triggerShareReward = () => {
        if (onClaimShareReward && !userQuizHistory[dateString]?.shareRewarded) {
            onClaimShareReward(dateString);
        }
    };

    const handleShareResults = () => {
        const accuracy = correctCount * 10;
        const badgeText = correctCount >= 8
            ? t_quiz('badgeTextSuccess')
            : t_quiz('badgeTextFailure');

        // Create visual emoji progress bars
        const incorrectCount = 10 - correctCount;
        const emojiBar = "🟩".repeat(correctCount) + "🟥".repeat(incorrectCount);

        const shareText = `🏆 ${t_quiz('dailyFootballQuiz')} 🏆\n📅 ${t_quiz('date')}: ${dateString}\n🎯 ${t_quiz('yourPointsEarned')}: ${score} PTS (${correctCount}/10 ${t_quiz('correctAnswersShort')}, ${accuracy}% Accuracy)\n📊 Progress: ${emojiBar}\n\n${badgeText}\n\nJoin the challenge, collect players, trade & play! ⚽🔥\n👉 https://paganini2026.com/`;

        if (navigator.share) {
            navigator.share({
                title: t_quiz('dailyFootballQuiz'),
                text: shareText,
                url: 'https://paganini2026.com/'
            })
                .then(() => {
                    setShared(true);
                    triggerShareReward();
                    setTimeout(() => setShared(false), 2500);
                })
                .catch(err => {
                    console.warn("Direct share API cancelled or failed:", err);
                    if (err.name !== 'AbortError') {
                        fallbackCopyText(shareText);
                    }
                });
        } else {
            fallbackCopyText(shareText);
        }
    };

    const fallbackCopyText = (text: string) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => {
                    setCopied(true);
                    triggerShareReward();
                    setTimeout(() => setCopied(false), 2500);
                })
                .catch(err => {
                    console.error("Clipboard copy failed:", err);
                    legacyCopyText(text);
                });
        } else {
            legacyCopyText(text);
        }
    };

    const legacyCopyText = (text: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            triggerShareReward();
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            console.error("Legacy fallback copy failed:", err);
        }
        document.body.removeChild(textArea);
    };

    // 'idle' | 'playing' | 'revealing' | 'completed'
    const [quizStatus, setQuizStatus] = useState<'idle' | 'playing' | 'revealing' | 'completed'>('idle');
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
    const [confirmReset, setConfirmReset] = useState(false);
    const [syncStatus, setSyncStatus] = useState<string>('idle');

    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize questions dynamically from server with local fail-safe selection
    useEffect(() => {
        let active = true;
        const fetchQuestions = async () => {
            setLoadingQuestions(true);
            try {
                const res = await fetch(`/api/quiz/questions?date=${dateString}&lang=${lang}`);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                const data = await res.json();
                if (active) {
                    if (Array.isArray(data.questions)) {
                        setDailyQuestions(data.questions);
                    } else {
                        throw new Error("Invalid structure returned for daily questions list");
                    }
                }
            } catch (err) {
                console.error("Error loading daily quiz from custom API endpoint, employing local fallback scheduler:", err);
                if (active) {
                    const fallbackList = getDailyQuestions(dateString, lang);
                    setDailyQuestions(fallbackList);
                }
            } finally {
                if (active) {
                    setLoadingQuestions(false);
                }
            }
        };

        fetchQuestions();

        // Check if user has already played today
        const todayRecord = userQuizHistory[dateString];
        if (todayRecord && typeof todayRecord.score === 'number') {
            setQuizStatus('completed');
            setScore(todayRecord.score);
            setCorrectCount(todayRecord.correctCount ?? (todayRecord.score <= 10 ? todayRecord.score : 8));
            // Construct approximate answers list to display recap if desired
            const stubAnswers = new Array(10).fill("COMPLETED");
            setUserAnswers(stubAnswers);
        }

        return () => {
            active = false;
        };
    }, [dateString, userQuizHistory, lang]);

    // Load scoreboard dynamically on mount and when histories update
    useEffect(() => {
        fetchScoreboard();
    }, [dateString, userQuizHistory]);

    // Synchronize produced questions to firebase database (restricted to superadmin)
    useEffect(() => {
        const syncQuestions = async () => {
            // Only super admin is permitted to push the production questions catalog to Firestore
            if (!user || user.email !== 'gonzos978@gmail.com') return;

            setSyncStatus('Running background sync... 🕒');
            try {
                console.log("Super admin detected, syncing question catalog with firestore...");
                const batch = writeBatch(db);

                ALL_QUESTIONS.forEach((q) => {
                    const qRef = doc(db, 'quizquestions', q.id);
                    const qData = {
                        id: q.id,
                        theme: q.theme || "Sport",
                        difficulty: q.difficulty || "medium",
                        question: q.question,
                        correctAnswer: q.answer,
                        answerA: q.options[0] || "",
                        answerB: q.options[1] || "",
                        ...(q.options[2] ? { answerC: q.options[2] } : {}),
                        ...(q.options[3] ? { answerD: q.options[3] } : {}),
                        ...(q.type ? { type: q.type } : {}),
                        ...(q.avatarEmoji ? { avatarEmoji: q.avatarEmoji } : {}),
                        ...(q.crests ? { crests: q.crests } : {})
                    };

                    batch.set(qRef, qData, { merge: true });
                });

                await batch.commit();
                console.log("Successfully synchronized all questions in database!");
                setSyncStatus('Successfully synchronized 20 questions! ✅');
            } catch (e: any) {
                console.error("Failed to sync production questions to Firestore:", e);
                setSyncStatus(`Sync failed: ${e?.message || e} ❌`);
            }
        };

        syncQuestions();
    }, [user]);

    // Handle active countdown timer when playing
    useEffect(() => {
        if (quizStatus !== 'playing') {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }

        setTimer(10);

        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    handleTimeOut();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [quizStatus, currentIdx]);

    const handleTimeOut = () => {
        // Save timeout result
        setIsAnswerCorrect(false);
        setSelectedAnswer("TIMEOUT");
        setQuizStatus('revealing');

        const nextAnswers = [...userAnswers];
        nextAnswers[currentIdx] = "TIMEOUT";
        setUserAnswers(nextAnswers);

        // Stay on reveal state for 1.8 seconds, then move to next
        setTimeout(() => {
            advanceQuiz(nextAnswers, score, correctCount);
        }, 1800);
    };

    const selectAnswer = (option: string) => {
        if (quizStatus !== 'playing') return;

        if (timerRef.current) clearInterval(timerRef.current);

        const correctAns = dailyQuestions[currentIdx].answer;
        const isCorrect = option === correctAns;

        setSelectedAnswer(option);
        setIsAnswerCorrect(isCorrect);
        setQuizStatus('revealing');

        const nextAnswers = [...userAnswers];
        nextAnswers[currentIdx] = option;
        setUserAnswers(nextAnswers);

        const pointsEarned = isCorrect ? (1 + timer) : 0;
        const nextScore = score + pointsEarned;
        const nextCorrectCount = isCorrect ? correctCount + 1 : correctCount;

        if (isCorrect) {
            setScore(nextScore);
            setCorrectCount(nextCorrectCount);
        }

        // Delay 1.8 seconds to showcase incorrect/correct option status animations
        setTimeout(() => {
            advanceQuiz(nextAnswers, nextScore, nextCorrectCount);
        }, 1800);
    };

    const advanceQuiz = async (latestAnswers: (string | null)[], currentRunningScore: number, currentCorrectCount: number) => {
        setSelectedAnswer(null);
        setIsAnswerCorrect(null);

        if (currentIdx < 9) {
            setCurrentIdx(prev => prev + 1);
            setQuizStatus('playing');
        } else {
            // Quiz complete!
            setQuizStatus('completed');

            const didWinReward = currentCorrectCount >= 8; // 80% correct threshold (8/10)

            // Save result immediately to user profile in DB or localStorage
            if (onSaveQuizResult) {
                await onSaveQuizResult(dateString, currentRunningScore, didWinReward, currentCorrectCount);
            }

            if (didWinReward) {
                // Increment stickers packs by 1
                setPacks(packs + 1);
            }
        }
    };

    const startQuizService = () => {
        // Reset temporary states
        setCurrentIdx(0);
        setScore(0);
        setCorrectCount(0);
        setUserAnswers(new Array(10).fill(null));
        setSelectedAnswer(null);
        setIsAnswerCorrect(null);
        setQuizStatus('playing');
    };

    const activeQuestion = dailyQuestions[currentIdx];
    const isTodayRewarded = userQuizHistory[dateString]?.rewarded;
    const isTodayPlayed = !!userQuizHistory[dateString];

    return (
        <div className="max-w-3xl mx-auto p-4 sm:p-6 select-none" id="quiz-page-container">
            {/* Title block */}
            <div className="mb-6 text-center" id="quiz-header">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-2">
                    <Sparkles size={13} className="text-amber-400 rotate-12" />
                    <span className="font-mono text-[9px] font-bold text-amber-400 uppercase tracking-widest">{t_quiz('digitalQuizEvent')}</span>
                </div>
                <h2 className="font-sans font-black text-2xl sm:text-3xl text-white uppercase tracking-tight" id="quiz-main-title">
                    {t_quiz('dailyFootballQuiz')}
                </h2>
                <p className="text-[11px] text-slate-400 font-normal max-w-md mx-auto mt-1">
                    {t_quiz('quizSubtitle')}
                </p>
            </div>

            <AnimatePresence mode="wait">
                {loadingQuestions ? (
                    <motion.div
                        key="quiz-loading"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="bg-[#1e293b] border border-slate-700/85 rounded-xl p-8 text-center shadow-2xl mb-6 flex flex-col items-center justify-center min-h-[380px] relative overflow-hidden"
                        id="quiz-questions-loader"
                    >
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />
                        <div className="w-16 h-16 rounded-full border-4 border-slate-800 border-t-blue-500 animate-spin flex items-center justify-center mb-6">
                            <RefreshCw size={24} className="text-blue-500 animate-pulse" />
                        </div>

                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full mb-3">
              <Sparkles size={11} className="text-blue-400 animate-bounce" />
              <span className="font-mono text-[9px] font-bold text-blue-400 uppercase tracking-widest">{t_quiz('aiBotActive')}</span>
            </span>
                        <h3 className="text-sm font-black text-slate-100 uppercase tracking-widest mb-2 font-mono" id="loader-title">
                            {t_quiz('curatingTrivia')}
                        </h3>
                        <p className="text-[11px] text-slate-400 max-w-xs leading-relaxed font-sans" id="loader-desc">
                            {t_quiz('loaderDesc')}
                        </p>
                    </motion.div>
                ) : quizStatus === 'idle' && (
                    <motion.div
                        key="quiz-idle"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        className="bg-[#1e293b] border border-slate-700/80 rounded-xl p-6 text-center shadow-xl mb-6 relative overflow-hidden"
                        id="quiz-intro-card"
                    >
                        {/* Background absolute visuals */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />

                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 bg-blue-600/10 border border-blue-500/20 text-blue-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/5" id="quiz-intro-trophy-container">
                                <Trophy size={36} className="animate-pulse" />
                            </div>
                        </div>

                        <h3 className="text-lg font-bold text-slate-100 uppercase tracking-tight mb-2">{t_quiz('readyTitle')}</h3>

                        <div className="max-w-md mx-auto bg-slate-950/40 rounded-lg p-4 border border-slate-800 text-left space-y-3 mb-6 font-sans">
                            <div className="flex items-start gap-2.5">
                                <div className="w-5 h-5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 flex items-center justify-center font-mono text-[10px] font-bold shrink-0 mt-0.5">1</div>
                                <p className="text-[11px] text-slate-350 leading-relaxed">
                                    <strong>{t_quiz('questionsTitle')}</strong> {t_quiz('questionsDesc')}
                                </p>
                            </div>

                            <div className="flex items-start gap-2.5">
                                <div className="w-5 h-5 bg-amber-500/10 border border-amber-500/20 rounded text-amber-400 flex items-center justify-center font-mono text-[10px] font-bold shrink-0 mt-0.5">2</div>
                                <p className="text-[11px] text-slate-350 leading-relaxed">
                                    <strong>{t_quiz('timerTitle')}</strong> {t_quiz('timerDesc')}
                                </p>
                            </div>

                            <div className="flex items-start gap-2.5">
                                <div className="w-5 h-5 bg-emerald-500/10 border border-emerald-500/20 rounded text-emerald-400 flex items-center justify-center font-mono text-[10px] font-bold shrink-0 mt-0.5">3</div>
                                <p className="text-[11px] text-slate-350 leading-relaxed">
                                    <strong>{t_quiz('rewardsTitle')}</strong> {t_quiz('rewardsDesc')}
                                </p>
                            </div>
                        </div>

                        {isTodayPlayed ? (
                            <div className="space-y-4" id="quiz-today-done-panel">
                                <div className="px-4 py-3 bg-slate-900/60 rounded border border-slate-800 flex items-center justify-between max-w-sm mx-auto">
                                    <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-slate-500">{t_quiz('todayStatus')}</span>
                                    {isTodayRewarded ? (
                                        <span className="text-[10px] font-mono font-bold tracking-tight bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">{t_quiz('rewardedStatus')}</span>
                                    ) : (
                                        <span className="text-[10px] font-mono font-bold tracking-tight bg-slate-800 text-slate-400 px-2 py-0.5 rounded">{t_quiz('completedStatus')} ({score} PTS)</span>
                                    )}
                                </div>
                                <p className="text-[11px] text-slate-400">
                                    {t_quiz('alreadyDoneDesc')}
                                </p>
                                <div className="pt-2 flex justify-center gap-3">
                                    <button
                                        onClick={startQuizService}
                                        className="px-5 py-2.5 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 font-mono text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all border border-slate-700 active:scale-95 cursor-pointer"
                                        id="quiz-replay-dev-btn"
                                    >
                                        {t_quiz('practiceRun')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={startQuizService}
                                className="w-full max-w-xs py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs font-bold uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-blue-500/20 cursor-pointer hover:shadow-blue-500/30 active:scale-95 flex items-center justify-center gap-2 mx-auto"
                                id="quiz-start-cta-btn"
                            >
                                <span>{t_quiz('initiateQuiz')}</span>
                                <ArrowRight size={14} className="animate-pulse" />
                            </button>
                        )}
                    </motion.div>
                )}

                {/* ACTIVE QUIZ MODE */}
                {(quizStatus === 'playing' || quizStatus === 'revealing') && activeQuestion && (
                    <motion.div
                        key="quiz-run-screen"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col gap-4"
                    >
                        {/* Top Stats and Timer bar */}
                        <div className="flex items-center justify-between bg-[#1e293b] border border-slate-700 px-4 py-3 rounded-xl shadow-lg" id="quiz-info-bar">
                            <div className="flex flex-col">
                                <span className="font-mono text-[9px] uppercase tracking-wider text-slate-550 leading-none">{t_quiz('questionTracker')}</span>
                                <span className="text-sm font-black font-mono text-white tracking-widest mt-1">
                  {(currentIdx + 1).toString().padStart(2, '0')} <span className="text-slate-600">/</span> 10
                </span>
                            </div>

                            {/* Countdown Timer Graphic */}
                            <div className="flex items-center gap-2.5 shrink-0">
                                <div className="flex flex-col items-end">
                                    <span className="font-mono text-[9px] uppercase tracking-wider text-slate-550 leading-none">{t_quiz('timeRemaining')}</span>
                                    <span className={`text-base font-mono font-black tracking-tight mt-1 ${timer <= 3 ? 'text-red-500 animate-bounce' : 'text-amber-400'}`}>
                    {timer}s
                  </span>
                                </div>
                                <div className="relative w-9 h-9 flex items-center justify-center rounded-full bg-slate-900 border border-slate-800">
                                    <Timer size={16} className={timer <= 3 ? "text-red-500 animate-spin" : "text-slate-400"} />
                                    {/* Outer circle decay animation */}
                                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-500 rotate-45 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* Horizontal Line timer bar */}
                        <div className="w-full h-1 bg-slate-900 rounded-full overflow-hidden">
                            <motion.div
                                style={{ width: `${(timer / 10) * 100}%` }}
                                className={`h-full transition-all duration-1000 ${timer <= 4 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : 'bg-blue-500'}`}
                            />
                        </div>

                        {/* Question Card Display */}
                        <div className="bg-[#1e293b] border border-slate-700/80 rounded-xl p-5 shadow-xl relative" id="active-question-card">
              <span className="absolute top-4 right-4 bg-slate-950/50 px-2 py-0.5 rounded border border-slate-800 text-[8px] font-mono tracking-widest uppercase text-slate-550">
                {t_quiz('type')}: {activeQuestion.type === 'abc' ? t_quiz('multipleChoice') : activeQuestion.type === 'yesno' ? t_quiz('trueOrFalse') : activeQuestion.type === 'player_image' ? t_quiz('playerSilhouette') : t_quiz('clubTransferLine')}
              </span>

                            <h4 className="font-sans font-bold text-[14px] sm:text-base text-slate-100 leading-snug pr-20 mt-1 mb-4">
                                {activeQuestion.question}
                            </h4>

                            {/* Conditional renderings depending on quiz type */}
                            {activeQuestion.type === 'player_image' && activeQuestion.avatarEmoji && (
                                <div className="mb-4 bg-slate-950/60 rounded-lg p-5 border border-slate-800 flex flex-col items-center justify-center text-center select-none shadow-inner" id="visual-emoji-sub-card">
                                    <div className="text-4xl sm:text-5xl mb-3 shadow-inner filter drop-shadow">⚽</div>
                                    <div className="bg-[#1e293b] border border-slate-800 rounded px-4 py-2 mt-1">
                                        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-550 mb-1 leading-none">{t_quiz('playerAchievementsProfile')}</p>
                                        <p className="text-sm font-semibold tracking-wide text-blue-400 font-sans leading-relaxed">
                                            {activeQuestion.avatarEmoji}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {activeQuestion.type === 'club_crest' && activeQuestion.crests && (
                                <div className="mb-5" id="visual-crests-sub-card">
                                    <p className="font-mono text-[8px] uppercase tracking-wider text-slate-555 text-center mb-2.5">{t_quiz('seniorTransferHistory')}</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        {activeQuestion.crests.map((cr, idx) => (
                                            <div key={idx} className={`relative rounded-lg p-3 border shadow-sm flex flex-col items-center justify-center text-center gap-1.5 ${cr.color}`}>
                                                <div className="w-10 h-10 rounded-full border border-current flex items-center justify-center opacity-85 text-xs font-black font-mono shrink-0 shadow">
                                                    {cr.abbreviation}
                                                </div>
                                                <span className="font-sans font-bold text-[11px] leading-tight truncate w-full">{cr.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Answers Grid Options */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" id="quiz-options-grid">
                                {activeQuestion.options.map((opt) => {
                                    const isSelected = selectedAnswer === opt;
                                    const isCurrentCorrectOption = activeQuestion.answer === opt;

                                    let optionDesign = "bg-[#0f172a] border-slate-800 text-slate-350 hover:bg-slate-900 hover:border-slate-700 cursor-pointer";

                                    if (quizStatus === 'revealing') {
                                        if (isCurrentCorrectOption) {
                                            // Highlight correct option is green
                                            optionDesign = "bg-emerald-950/80 border-emerald-500 text-emerald-300 pointer-events-none";
                                        } else if (isSelected) {
                                            // Selected wrong answer
                                            optionDesign = "bg-red-950/80 border-red-500 text-red-350 pointer-events-none";
                                        } else {
                                            // Unselected non-correct
                                            optionDesign = "bg-[#0f172a] opacity-40 border-slate-900 pointer-events-none text-slate-600";
                                        }
                                    }

                                    return (
                                        <button
                                            key={opt}
                                            onClick={() => selectAnswer(opt)}
                                            disabled={quizStatus === 'revealing'}
                                            className={`w-full text-left px-4 py-3 border-2 rounded-xl text-xs font-semibold leading-normal font-sans flex items-center justify-between transition-all duration-300 hover:scale-[1.01] active:scale-95 ${optionDesign}`}
                                        >
                                            <span>{opt}</span>
                                            {quizStatus === 'revealing' && (
                                                <div>
                                                    {isCurrentCorrectOption ? (
                                                        <CheckCircle size={15} className="text-emerald-400 shrink-0" />
                                                    ) : isSelected ? (
                                                        <XCircle size={15} className="text-red-400 shrink-0" />
                                                    ) : null}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Answer reveal status box */}
                        <AnimatePresence>
                            {quizStatus === 'revealing' && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="bg-slate-950/40 border border-slate-805 rounded-xl px-4 py-3 flex items-center justify-center gap-2 font-mono text-[10px] font-bold uppercase tracking-wide text-center"
                                    id="quiz-live-reveal-toast"
                                >
                                    {isAnswerCorrect ? (
                                        <span className="text-emerald-400 flex items-center gap-1.5 justify-center">
                      <CheckCircle size={11} /> {t_quiz('excellentAnswer')}
                    </span>
                                    ) : selectedAnswer === 'TIMEOUT' ? (
                                        <span className="text-red-400 flex items-center gap-1.5 justify-center">
                      <Timer size={11} className="animate-spin" /> {t_quiz('timerExpired')} {activeQuestion.answer}
                    </span>
                                    ) : (
                                        <span className="text-red-400 flex items-center gap-1.5 justify-center">
                      <XCircle size={11} /> {t_quiz('wrongAnswer')} {activeQuestion.answer}
                    </span>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* QUIZ COMPLETED MODE */}
                {quizStatus === 'completed' && (
                    <motion.div
                        key="quiz-finished-screen"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="bg-[#1e293b] border border-slate-700/80 rounded-xl p-6 text-center shadow-2xl relative"
                        id="quiz-finished-card"
                    >
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

                        <div className="flex justify-center mb-4">
                            {correctCount >= 8 ? (
                                <div className="relative">
                                    <div className="w-20 h-20 bg-emerald-600/10 border border-emerald-500/30 text-emerald-400 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/10 relative z-10" id="victory-badge-box">
                                        <Trophy size={42} className="animate-bounce" />
                                    </div>
                                    <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-amber-500 text-slate-950 rounded-full flex items-center justify-center text-xs font-bold font-mono tracking-tighter border-2 border-[#1e293b] animate-pulse">
                                        ★
                                    </div>
                                </div>
                            ) : (
                                <div className="w-18 h-18 bg-slate-850/50 border border-slate-800 text-slate-500 rounded-2xl flex items-center justify-center" id="try-again-badge-box">
                                    <HelpCircle size={36} />
                                </div>
                            )}
                        </div>

                        <h3 className="text-xl font-black text-slate-100 uppercase tracking-tight mb-1" id="quiz-completed-badge">
                            {correctCount >= 8 ? t_quiz('challengeConquered') : t_quiz('challengeCompleted')}
                        </h3>

                        <p className="font-mono text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-4">
                            {t_quiz('date')}: {dateString}
                        </p>

                        {/* Score Ring Display */}
                        <div className="max-w-xs mx-auto bg-slate-950/50 border border-slate-850 p-4 rounded-xl shadow-inner mb-6 flex flex-col items-center">
                            <span className="font-mono text-[8px] uppercase tracking-widest text-slate-505 mb-1">{t_quiz('yourPointsEarned')}</span>
                            <span className={`text-4xl font-extrabold font-mono tracking-tight ${correctCount >= 8 ? 'text-emerald-400' : 'text-amber-500'}`}>
                {score} PTS
              </span>
                            <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                ({correctCount === 1 ? t_quiz('correctAnswersSingular', { correctCount }) : t_quiz('correctAnswers', { correctCount })})
              </span>
                        </div>

                        {/* Rewards Notification logic */}
                        {correctCount >= 8 ? (
                            <div className="space-y-4 max-w-sm mx-auto mb-4" id="victory-outcome-panel">
                                <div className="p-3 bg-emerald-950/30 border border-emerald-800/80 rounded-lg text-left">
                                    <div className="flex items-center gap-2 text-emerald-400 mb-1.5">
                                        <Award size={15} />
                                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider">{t_quiz('rewardUnlockedTitle')}</span>
                                    </div>
                                    <p className="text-[11px] text-slate-350 leading-relaxed font-sans">
                                        {t_quiz('rewardUnlockedDesc')}
                                    </p>
                                </div>

                                <p className="text-[11px] text-slate-400 italic">
                                    {t_quiz('rewardOnePerDayNote')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 max-w-sm mx-auto mb-6" id="under-par-outcome-panel">
                                <div className="p-3 bg-red-950/30 border border-red-900/60 rounded-lg text-left">
                                    <div className="flex items-center gap-2 text-red-400 mb-1">
                                        <XCircle size={15} />
                                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider">{t_quiz('underThresholdTitle')}</span>
                                    </div>
                                    <p className="text-[11px] text-slate-350 leading-relaxed font-sans mt-1">
                                        {t_quiz('underThresholdDesc', { correctCount })}
                                    </p>
                                </div>

                                <p className="text-[11px] text-slate-400 leading-normal font-sans">
                                    {t_quiz('underThresholdNote')}
                                </p>
                            </div>
                        )}

                        {/* CTAs */}
                        <div className="flex flex-col items-center gap-3 pt-2" id="quiz-ctas-panel">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
                                <button
                                    onClick={handleShareResults}
                                    className={`w-full sm:w-auto px-6 py-2.5 font-mono text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer border ${
                                        shared
                                            ? "bg-emerald-600 border-emerald-500 text-white"
                                            : copied
                                                ? "bg-amber-600 border-amber-500 text-white"
                                                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 border-transparent text-white shadow-lg shadow-blue-500/10"
                                    }`}
                                    id="quiz-share-btn"
                                >
                                    {shared || copied ? <Check size={14} className="animate-pulse" /> : <Share2 size={14} />}
                                    <span>
                    {shared
                        ? t_quiz('sharedSuccess')
                        : copied
                            ? t_quiz('copiedClipboard')
                            : t_quiz('directShare')
                    }
                  </span>
                                </button>

                                {correctCount < 8 && (
                                    <button
                                        onClick={startQuizService}
                                        className="w-full sm:w-auto px-6 py-2.5 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 font-mono text-[11px] font-semibold uppercase tracking-wider rounded-lg transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer border border-slate-700"
                                        id="quiz-retry-btn"
                                    >
                                        <RefreshCw size={13} className="animate-spin-slow" />
                                        <span>{t_quiz('retryQuiz')}</span>
                                    </button>
                                )}
                            </div>

                            <div className="w-full max-w-sm text-center px-4 py-2 bg-slate-900/40 border border-slate-800/60 rounded-lg shadow-sm">
                                {userQuizHistory[dateString]?.shareRewarded ? (
                                    <p className="text-[11px] text-emerald-400 font-mono flex items-center justify-center gap-1.5 font-medium uppercase tracking-wider">
                                        <span>{t_quiz('bonusClaimed')}</span>
                                    </p>
                                ) : (
                                    <p className="text-[11px] text-amber-350 font-mono flex items-center justify-center gap-1.5 font-semibold uppercase tracking-wider animate-pulse">
                                        <span>{t_quiz('bonusOffer')}</span>
                                    </p>
                                )}
                            </div>

                            {isTodayPlayed && (
                                <div className="w-full flex flex-col items-center gap-4 mt-3">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {t_quiz('comeBackTomorrow')}
                  </span>
                                    {onResetQuiz && user?.email === 'gonzos978@gmail.com' && (
                                        <div className="w-full flex flex-col items-center gap-2">
                                            {!confirmReset ? (
                                                <button
                                                    onClick={() => setConfirmReset(true)}
                                                    className="px-4 py-2.5 bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 hover:text-rose-300 border border-rose-900/40 font-mono text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all active:scale-95 cursor-pointer"
                                                    id="dev-reset-quiz-btn"
                                                >
                                                    {t_quiz('resetBtn')}
                                                </button>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2.5 p-3.5 rounded-xl border border-rose-900/45 bg-slate-950/95 max-w-xs w-full shadow-lg">
                                                    <p className="text-[10px] font-mono font-bold text-rose-350 uppercase tracking-tight text-center leading-normal">
                                                        {t_quiz('confirmResetProgress')}
                                                    </p>
                                                    <div className="flex items-center justify-center gap-2.5 w-full mt-1">
                                                        <button
                                                            onClick={async () => {
                                                                await onResetQuiz(dateString);
                                                                setQuizStatus('idle');
                                                                setScore(0);
                                                                setCorrectCount(0);
                                                                setUserAnswers(new Array(10).fill(null));
                                                                setSelectedAnswer(null);
                                                                setIsAnswerCorrect(null);
                                                                setCurrentIdx(0);
                                                                setConfirmReset(false);
                                                            }}
                                                            className="px-3 py-1.5 bg-rose-650 hover:bg-rose-550 text-white font-mono text-[10px] font-black uppercase tracking-wider rounded-md transition-all cursor-pointer shadow-sm shadow-rose-950/45"
                                                            id="dev-reset-quiz-btn-confirm"
                                                        >
                                                            {t_quiz('yesReset')}
                                                        </button>
                                                        <button
                                                            onClick={() => setConfirmReset(false)}
                                                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-mono text-[10px] font-bold uppercase tracking-wider rounded-md transition-all cursor-pointer"
                                                            id="dev-reset-quiz-btn-cancel"
                                                        >
                                                            {t_quiz('cancel')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* SCOREBOARD SECTION */}
            {(quizStatus === 'idle' || quizStatus === 'completed') && (
                <div className="mt-8 bg-[#1e293b]/70 border border-slate-700/50 rounded-2xl p-5 sm:p-6 shadow-xl relative backdrop-blur-sm" id="quiz-scoreboard-panel">

                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-800 mb-5" id="scoreboard-header">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/20 shadow-md">
                                <Trophy size={18} className="animate-pulse" />
                            </div>
                            <div>
                                <h4 className="font-sans font-black text-slate-100 text-[15px] sm:text-base uppercase tracking-tight">
                                    {t_quiz('quizLeaderboard')}
                                </h4>
                                <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase mt-0.5">
                                    {t_quiz('liveRankings')}
                                </p>
                            </div>
                        </div>

                        {/* Refresh button */}
                        <button
                            onClick={fetchScoreboard}
                            disabled={loadingScoreboard}
                            className="self-start sm:self-auto px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 hover:text-white font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg border border-slate-750 flex items-center gap-2 transition active:scale-95 cursor-pointer shadow-sm"
                            id="scoreboard-refresh-btn"
                        >
                            <RefreshCw size={12} className={loadingScoreboard ? "animate-spin" : ""} />
                            <span>{loadingScoreboard ? t_quiz('loading') : t_quiz('refreshStats')}</span>
                        </button>
                    </div>

                    {/* Tabs & Search controls */}
                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-5" id="scoreboard-controls">
                        {/* Toggle tabs */}
                        <div className="flex bg-slate-950/60 p-1 rounded-xl border border-slate-800/80 self-start w-full md:w-auto">
                            <button
                                onClick={() => setScoreboardTab('today')}
                                className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-mono text-[10px] font-black uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                                    scoreboardTab === 'today'
                                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/10"
                                        : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {t_quiz('todaysRanks')}
                            </button>
                            <button
                                onClick={() => setScoreboardTab('alltime')}
                                className={`flex-1 md:flex-none px-4 py-2 rounded-lg font-mono text-[10px] font-black uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                                    scoreboardTab === 'alltime'
                                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/10"
                                        : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {t_quiz('allTimeRecords')}
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full md:max-w-xs">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                                <Search size={12} />
                            </div>
                            <input
                                type="text"
                                placeholder={t_quiz('searchCollectors')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-slate-950/40 border border-slate-800/80 text-slate-200 placeholder-slate-650 text-[10px] font-mono font-bold uppercase tracking-widest pl-9 pr-4 py-2 rounded-xl focus:outline-none focus:border-blue-500/50 focus:bg-slate-950/70 transition-all text-ellipsis"
                            />
                        </div>
                    </div>

                    {/* Leaderboard user list */}
                    {loadingScoreboard ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3 bg-slate-950/15 border border-slate-850/40 rounded-xl">
                            <RefreshCw size={24} className="text-blue-500 animate-spin" />
                            <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest leading-none">{t_quiz('retrievingLeaderboard')}</span>
                        </div>
                    ) : filteredScoreboard.length === 0 ? (
                        <div className="text-center py-12 bg-slate-950/20 border border-slate-850/40 rounded-xl px-4">
                            <User size={24} className="text-slate-600 mx-auto mb-2 opacity-50" />
                            <p className="text-slate-400 text-xs font-sans">
                                {searchTerm.trim() !== ''
                                    ? t_quiz('noMatchedPlayers')
                                    : scoreboardTab === 'today'
                                        ? t_quiz('nobodySubmitted')
                                        : t_quiz('noRankings')
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 thin-scrollbar" id="scoreboard-entries-list">
                            {filteredScoreboard.map((entry, idx) => {
                                const isMe = user && (entry.userId === user.uid);
                                const rank = idx + 1;

                                // Colors based on podium ranking
                                let rankStyle = "text-slate-400 bg-slate-900/60 border-slate-800/50";
                                if (rank === 1) rankStyle = "text-amber-400 bg-amber-500/10 border-amber-500/25 font-black";
                                else if (rank === 2) rankStyle = "text-slate-200 bg-slate-350/10 border-slate-300/25 font-black";
                                else if (rank === 3) rankStyle = "text-amber-600 bg-amber-700/10 border-amber-650/25 font-black";

                                return (
                                    <div
                                        key={entry.userId}
                                        className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 ${
                                            isMe
                                                ? "bg-blue-600/10 border-blue-500/30 shadow-lg shadow-blue-500/5"
                                                : "bg-slate-950/15 border-slate-850/60 hover:bg-slate-950/30 hover:border-slate-800/80"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {/* Rank Emblem */}
                                            <div className={`w-7 h-7 rounded-lg text-xs font-mono flex items-center justify-center border shadow-sm ${rankStyle}`}>
                                                {rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : rank}
                                            </div>

                                            {/* User Avatar */}
                                            <Avatar photoURL={entry.photoURL || 'wc_ball'} email={entry.email} size="sm" />

                                            {/* Display name & Stats tracker */}
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                          <span className={`text-[12px] font-extrabold tracking-tight ${isMe ? 'text-blue-400' : 'text-slate-200'}`}>
                            {entry.displayName}
                          </span>
                                                    {isMe && (
                                                        <span className="text-[8px] bg-blue-500/20 border border-blue-500/30 text-blue-400 px-1.5 py-0.5 rounded-md font-mono font-bold tracking-wider uppercase shadow-inner">
                              {t_quiz('you')}
                            </span>
                                                    )}
                                                </div>
                                                <span className="text-[9px] text-slate-500 font-mono tracking-wider leading-none mt-1 uppercase">
                          {entry.quizzesPlayed === 1 ? `1 ${t_quiz('eventsSolvedSingular')}` : `${entry.quizzesPlayed} ${t_quiz('eventsSolvedPlural')}`}
                        </span>
                                            </div>
                                        </div>

                                        {/* Performance metrics display */}
                                        <div className="text-right shrink-0">
                                            {scoreboardTab === 'today' ? (
                                                <div className="flex flex-col items-end">
                          <span className="text-[12px] font-bold font-mono text-emerald-400 leading-tight">
                            {entry.todayScore} PTS
                          </span>
                                                    <span className="text-[8px] text-slate-500 font-mono mt-0.5 tracking-wider uppercase font-semibold">
                            {t_quiz('todaysPoints')}
                          </span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-end">
                          <span className="text-[12px] font-bold font-mono text-blue-400 leading-tight">
                            {entry.totalScore} PTS
                          </span>
                                                    <span className="text-[8px] text-slate-500 font-mono mt-0.5 tracking-wider uppercase font-semibold">
                            {t_quiz('totalPoints')}
                          </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* SUPER ADMIN WIDGET */}
            {user?.email === 'gonzos978@gmail.com' && (
                <div className="mt-8 p-4 bg-slate-900 border border-purple-900/40 rounded-2xl shadow-xl flex flex-col gap-3" id="super-admin-sync-console">
                    <div className="flex items-center justify-between border-b border-slate-850 pb-2.5">
                        <div className="flex items-center gap-2">
                            <span className="text-sm">⚡</span>
                            <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-purple-400">
                                Super Admin Console
                            </h4>
                        </div>
                        <span className="text-[9px] font-mono text-slate-500 font-bold uppercase tracking-widest bg-slate-950 px-2 py-0.5 rounded border border-slate-800">
              {user.email}
            </span>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-950/40 p-3 rounded-xl border border-slate-800/85">
                        <div>
                            <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wide">
                                Firestore Quiz Questions Collection
                            </p>
                            <p className="text-[11px] text-slate-300 mt-1 font-sans">
                                Status: <span className="font-mono text-xs font-bold text-purple-400">{syncStatus}</span>
                            </p>
                        </div>

                        <button
                            onClick={async () => {
                                setSyncStatus('Executing manual force-sync... ⚡');
                                try {
                                    const batch = writeBatch(db);
                                    ALL_QUESTIONS.forEach((q) => {
                                        const qRef = doc(db, 'quizquestions', q.id);
                                        const qData = {
                                            id: q.id,
                                            theme: q.theme || "Sport",
                                            difficulty: q.difficulty || "medium",
                                            question: q.question,
                                            correctAnswer: q.answer,
                                            answerA: q.options[0] || "",
                                            answerB: q.options[1] || "",
                                            ...(q.options[2] ? { answerC: q.options[2] } : {}),
                                            ...(q.options[3] ? { answerD: q.options[3] } : {}),
                                            ...(q.type ? { type: q.type } : {}),
                                            ...(q.avatarEmoji ? { avatarEmoji: q.avatarEmoji } : {}),
                                            ...(q.crests ? { crests: q.crests } : {})
                                        };
                                        batch.set(qRef, qData, { merge: true });
                                    });
                                    await batch.commit();
                                    setSyncStatus('Manually synced 20 questions successfully! ✅');
                                } catch (e: any) {
                                    setSyncStatus(`Sync failed: ${e?.message || e} ❌`);
                                }
                            }}
                            className="px-4 py-2 bg-purple-950/45 hover:bg-purple-900/60 border border-purple-900/40 hover:border-purple-800/60 text-purple-300 font-mono text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer active:scale-95"
                            id="admin-manual-sync-btn"
                        >
                            🔄 Force Sync Collection
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
